import React, { useState } from 'react';

interface ExportModalProps {
  onClose: () => void;
  onExport: (selectedColumns: string[]) => void;
  isLoading: boolean;
}

const allColumns = [
  { id: 'Sl.No', label: 'Sl.No', defaultSelected: true, locked: true },
  { id: 'Name', label: 'Name', defaultSelected: true, locked: true },
  { id: 'Phone', label: 'Phone', defaultSelected: true, locked: true },
  { id: 'Created At', label: 'Created At', defaultSelected: true, locked: true },
  { id: 'Updated At', label: 'Updated At', defaultSelected: true, locked: true },
  { id: 'Assigned To', label: 'Assigned To', defaultSelected: true, locked: true },
  { id: 'Source', label: 'Source', defaultSelected: false, locked: false },
  { id: 'Sub Source', label: 'Sub Source', defaultSelected: false, locked: false },
  { id: 'Campaign Name', label: 'Campaign Name', defaultSelected: false, locked: false },
  { id: 'Destination', label: 'Destination', defaultSelected: false, locked: false },
  { id: 'Sub Agent', label: 'Sub Agent', defaultSelected: false, locked: false },
  { id: 'Admission Status', label: 'Admission Status', defaultSelected: false, locked: false },
  { id: 'Sub Admission Status', label: 'Sub Admission Status', defaultSelected: false, locked: false },
  { id: 'Intake Month', label: 'Intake Month', defaultSelected: false, locked: false },
  { id: 'Intake Year', label: 'Intake Year', defaultSelected: false, locked: false },
  { id: 'Stage', label: 'Stage', defaultSelected: false, locked: false },
  { id: 'Sub Stage', label: 'Sub Stage', defaultSelected: false, locked: false },
  // { id: 'Priority', label: 'Priority', defaultSelected: false, locked: false },
  { id: 'Follow Up', label: 'Follow Up', defaultSelected: false, locked: false },
  { id: 'Remark', label: 'Remark', defaultSelected: false, locked: false },
  // Additional filter fields (22 total)
  { id: 'State', label: 'State', defaultSelected: false, locked: false },
  { id: 'Status', label: 'Status', defaultSelected: false, locked: false },
  { id: 'Sub Status', label: 'subStatus', defaultSelected: false, locked: false },
  { id: 'Welcome Call Status', label: 'Welcome Call Status', defaultSelected: false, locked: false },
  { id: 'Visa Process Status', label: 'Visa Process Status', defaultSelected: false, locked: false },
  { id: 'Application Status', label: 'Application Status', defaultSelected: false, locked: false },
  { id: 'Conditional Offer', label: 'Conditional Offer', defaultSelected: false, locked: false },
  { id: 'Unconditional Offer', label: 'Unconditional Offer', defaultSelected: false, locked: false },
  { id: 'Fee Payment Status', label: 'Fee Payment Status', defaultSelected: false, locked: false },
  { id: 'File Submission Status', label: 'File Submission Status', defaultSelected: false, locked: false },
  { id: 'Preferred Stream', label: 'Preferred Stream', defaultSelected: false, locked: false },
  { id: 'Super Agents', label: 'Super Agents', defaultSelected: false, locked: false },
  { id: 'Follow Up Date', label: 'Follow Up Date', defaultSelected: false, locked: false },
];

const ExportModal: React.FC<ExportModalProps> = ({ onClose, onExport, isLoading }) => {
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    allColumns.filter(col => col.defaultSelected).map(col => col.id)
  );

  const toggleColumn = (columnId: string) => {
    const column = allColumns.find(col => col.id === columnId);
    if (column?.locked) return;

    setSelectedColumns(prev =>
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  const handleExport = () => {
    onExport(selectedColumns);
  };

  const handleClose = () => {
    onClose();
  };


  const handleSelectAll = () => {
    const allSelectableColumns = allColumns
      .filter(col => !col.locked)
      .map(col => col.id);

    setSelectedColumns(prev => {
      // If all non-locked columns are already selected, deselect them
      const allNonLockedSelected = allSelectableColumns.every(col =>
        prev.includes(col)
      );

      if (allNonLockedSelected) {
        // Keep only the locked columns
        return prev.filter(col =>
          allColumns.find(c => c.id === col)?.locked
        );
      } else {
        // Add all non-locked columns (without duplicates)
        return Array.from(new Set([...prev, ...allSelectableColumns]));
      }
    });
  };

  const allNonLockedSelected = allColumns
    .filter(col => !col.locked)
    .every(col => selectedColumns.includes(col.id));


  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Export Data</h2>
            <p className="text-sm text-gray-600 mt-1">Select the columns you want to include in your export</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2 hover:bg-gray-100 rounded-full"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Select All Controls */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">
                {selectedColumns.length} of {allColumns.length} columns selected
              </span>
            </div>
            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${allNonLockedSelected
                ? 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-200'
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-200'
                }`}
              onClick={handleSelectAll}
            >
              {allNonLockedSelected ? 'Deselect All Optional' : 'Select All Optional'}
            </button>
          </div>
        </div>

        {/* Column Selection Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Required Columns */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                Required Columns
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {allColumns.filter(col => col.locked).map(column => (
                  <div key={column.id} className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <input
                      type="checkbox"
                      id={`col-${column.id}`}
                      checked={selectedColumns.includes(column.id)}
                      onChange={() => toggleColumn(column.id)}
                      className="mr-3 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                      disabled={column.locked}
                    />
                    <label
                      htmlFor={`col-${column.id}`}
                      className="text-sm font-medium text-blue-900 flex-1"
                    >
                      {column.label}
                      <span className="text-xs text-blue-600 block">Required field</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Optional Columns */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                Optional Columns
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {allColumns.filter(col => !col.locked).map(column => (
                  <div
                    key={column.id}
                    className={`flex items-center p-3 rounded-lg border transition-all duration-200 cursor-pointer hover:shadow-sm ${selectedColumns.includes(column.id)
                      ? 'bg-green-50 border-green-200 shadow-sm'
                      : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                    onClick={() => toggleColumn(column.id)}
                  >
                    <input
                      type="checkbox"
                      id={`col-${column.id}`}
                      checked={selectedColumns.includes(column.id)}
                      onChange={() => toggleColumn(column.id)}
                      onClick={() => toggleColumn(column.id)}
                      className="mr-3 h-4 w-4 text-green-600 rounded focus:ring-green-500 cursor-pointer"
                    />
                    <label
                      htmlFor={`col-${column.id}`}
                      className="text-sm font-medium text-gray-900 cursor-pointer flex-1"
                    >
                      {column.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Export will include <span className="font-semibold">{selectedColumns.length}</span> columns
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                disabled={isLoading}
                className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={isLoading || selectedColumns.length === 0}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export Data
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;